/**
 * Utility functions for checking SuperAdmin privileges
 */

/**
 * Check if user has SuperAdmin privileges
 * @param {Object} user - User object
 * @returns {boolean} - True if user is SuperAdmin
 */
export const isSuperAdmin = (user) => {
  if (!user) return false;

  // Check both isSuperAdmin flag and role
  return (
    user.isSuperAdmin === true ||
    (user.role && user.role.toLowerCase() === 'superadmin')
  );
};

/**
 * Check if SuperAdmin should bypass a specific restriction
 * @param {Object} user - User object
 * @param {string} feature - Feature name (optional)
 * @returns {boolean} - True if should bypass
 */
export const shouldBypassRestriction = (user, feature = null) => {
  // SuperAdmin bypasses all restrictions
  return isSuperAdmin(user);
};

/**
 * Check if user has specific role(s)
 * @param {Object} user - User object
 * @param {string|Array<string>} requiredRoles - Required role(s)
 * @returns {boolean} - True if user has required role
 */
export const hasRole = (user, requiredRoles) => {
  if (!user) return false;

  // SuperAdmin bypasses role checks
  if (isSuperAdmin(user)) return true;

  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  // Check user role
  if (user.role) {
    return roles.some(role => user.role.toLowerCase() === role.toLowerCase());
  }

  // Check user roles array
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(userRole =>
      roles.some(role => userRole.toLowerCase() === role.toLowerCase())
    );
  }

  return false;
};

/**
 * Check if user can access a feature
 * @param {Object} user - User object
 * @param {string} feature - Feature identifier
 * @param {Array<string>} allowedRoles - Roles allowed to access feature
 * @returns {boolean} - True if user can access
 */
export const canAccessFeature = (user, feature, allowedRoles = []) => {
  if (!user) return false;

  // SuperAdmin can access everything
  if (isSuperAdmin(user)) return true;

  // Check if user has required role
  if (allowedRoles.length > 0) {
    return hasRole(user, allowedRoles);
  }

  // Default: allow if authenticated
  return true;
};

/**
 * Get user display name
 * @param {Object} user - User object
 * @returns {string} - Display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Guest';

  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  if (user.firstName) return user.firstName;
  if (user.lastName) return user.lastName;
  if (user.email) return user.email.split('@')[0];

  return 'User';
};

/**
 * Get user badge/label based on role
 * @param {Object} user - User object
 * @returns {Object} - Badge info { label, color }
 */
export const getUserBadge = (user) => {
  if (!user) return null;

  if (isSuperAdmin(user)) {
    return { label: 'SuperAdmin', color: 'error' };
  }

  const role = user.role || (user.roles && user.roles[0]);
  if (!role) return null;

  const roleMap = {
    admin: { label: 'Admin', color: 'primary' },
    purchasemanager: { label: 'Purchase Manager', color: 'secondary' },
    buyer: { label: 'Buyer', color: 'warning' },
    inventorymanager: { label: 'Inventory Manager', color: 'info' },
    storekeeper: { label: 'Store Keeper', color: 'success' },
    sales: { label: 'Sales', color: 'error' },
    employee: { label: 'Employee', color: 'info' },
    supplier: { label: 'Supplier', color: 'warning' },
    customer: { label: 'Customer', color: 'success' },
    user: { label: 'User', color: 'default' },
  };

  return roleMap[role.toLowerCase()] || { label: role, color: 'default' };
};

export default {
  isSuperAdmin,
  shouldBypassRestriction,
  hasRole,
  canAccessFeature,
  getUserDisplayName,
  getUserBadge,
};
